import Avatar from "@mui/material/Avatar";
import { ReactNode } from "react";

type Props = {
  userId?: string | null;
  name?: string | null;
  size?: number;
  fontSize?: number;
  icon?: ReactNode;
  imageSrc?: string | null;
  imageAlt?: string | null;
  initialsOverride?: string;
  backgroundColor?: string;
  textColor?: string;
  ariaLabel?: string;
};

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const colors = [
    "#5fa3e0",
    "#e58a7a",
    "#7eb89a",
    "#b89acd",
    "#d4a574",
    "#6eb5b0",
    "#c98a9e",
    "#8ca8d4",
    "#a8b878",
    "#9a9ab8",
  ];
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name?: string | null): string => {
  if (!name) return "?";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0][0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
  return (first + last).toUpperCase() || "?";
};

export const UserAvatar = ({
  userId,
  name,
  size = 21,
  fontSize = 9,
  icon,
  imageSrc,
  imageAlt,
  initialsOverride,
  backgroundColor,
  textColor,
  ariaLabel,
}: Props) => {
  const colorKey = userId || name || "default";
  const initials = initialsOverride ?? getInitials(name);
  const avatarBackgroundColor =
    backgroundColor ?? (userId || name ? stringToColor(colorKey) : undefined);

  return (
    <Avatar
      aria-label={ariaLabel}
      alt={imageAlt ?? undefined}
      src={imageSrc ?? undefined}
      style={{
        width: size,
        height: size,
        fontSize,
        ...(avatarBackgroundColor
          ? { backgroundColor: avatarBackgroundColor }
          : {}),
        ...(textColor ? { color: textColor } : {}),
      }}
      sx={{
        width: `${size}px`,
        height: `${size}px`,
        fontSize: `${fontSize}px`,
        backgroundColor: avatarBackgroundColor ?? "action.active",
        color: textColor,
      }}
    >
      {userId || name ? initials : icon ?? "?"}
    </Avatar>
  );
};
